import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';
import { NO_VALUE, ValueFormat, formatPoints, formatSigned, formatStayDateLong, formatValue, roundPoints } from './chartFormat';
import { CHART_HEIGHT, ChartRole, ChartSize, colorOfRole } from './chartTokens';
import Sparkline, { sparklineChange } from './Sparkline';
import InfoTooltip from '../tooltip/InfoTooltip';

// A table of exact values that the user can sort: the rows behind a chart, or a list such as
// "the 10 dates with the largest difference to the compset".
// The look is the documented table pattern (Patterns/Table): sticky header, xs text, zebra rows.
// Each column has a format: text, date (Mon 7 Dec 2026) or a chart format (money, percent, number),
// so a table shows a value exactly as the chart next to it. Numbers align right.
// A click on a column header sorts by that column; a second click turns the order. A missing value
// is '–' and always goes last.
// A column can carry a chart role: then a short line in the colour of that role keys the column to
// its line in the chart. The 'series' columns take the series slots in their order, as in the chart.
// With `size`, the table scrolls inside the height of a chart of that size (for the table view of a
// ChartCard). On a narrow screen the table scrolls sideways inside its frame; the first column stays. The table knows nothing about the data: the caller gives the columns and the rows.
// Column groups put a header row above the columns ("Today", "Situation D-7"); a thin line separates the groups.
// A row with children opens and closes with the chevron in its first cell (a group and its segments); the children
// follow their row, indented on a gray-50 ground, and keep the order of the caller. Sorting moves only the top rows.
// A child can have children too (a second level: the properties under a rate plan); they indent one step more.
// With sortScope 'children', the top rows keep the order of the caller (fixed groups) and a sort orders the rows
// inside each open row, at each level. With sortCycle 'threeClicks', the third click on a header goes back to
// defaultSort. Row ids are unique across all levels.
// A "more" line ("+ 19 more, included in the total") follows the children of its row, or the top rows (above the
// footer), with an optional text button ("Show all 34").
// With rowLimit, each list (the top rows, and the children of each open row) shows its first `count` rows in the current
// order, and a line under it names the rest with a button "Show all"; then a button cuts the list again ("Show the top 15").
// A muted row (gray text) is a remainder such as "No segment". A footer row (the total) stays last, in bold.
// With wrapHeaders, a long column label wraps to more lines at the bottom of its header cell, so that many wide labels
// (hotel names) fit the width; off, a label stays on one line.
// A column with the tone 'change' shows a positive number in green-700, a negative one in red-700 and 0 in gray-400.
// A number column can hold a text value ("new"): the text shows as it is, with no tone, and sorts after the numbers.
// The format 'points' shows a difference of two shares in percentage points (0.081 → 8.1 pts, as the KpiTile change);
// with signed and the tone 'change' it is +8.1 pts in green or -3.6 pts in red. The tone follows the rounded value.
// The format 'sparkline' shows a list of values (oldest first) as a small line (Sparkline 'direction': red when the
// last value is below the first one). A screen reader reads "From 120 to 340" in the valueFormat of the column. A sort
// on the column orders by the change (last − first). A row with fewer than 2 values shows '–'.
// With onRowSelect, the name in the first cell is a text button (a click selects the row, for example to show it in the
// chart above); the chevron is then a button of its own that opens and closes the row. selectedRowId marks the selected
// row (aria-pressed, blue name). The footer is not selectable.
// onSortChange tells the caller the new order after a click on a header: the sort, and the ids of the top rows in the
// order that the table shows them.
// A column with the tone 'band' fills each cell with the first of its bands (from the highest) that the value reaches, for
// example five occupancy bands; with outlineAbove, a value above it also gets a red ring. A column with the tone 'chip' shows
// its value in a small chip, in the tone that the row gives the cell (cellTones: success, warning, critical, neutral).
// A row can be the current one (a blue ground and a blue line on its left, "today"), dimmed (the past), and emphasized
// (a gray-100 first cell in bold, "a weekend").
// With onRowsSelect, the user selects several rows: a press on a row and a drag up or down selects the rows from the first
// to the last; a click adds or removes one row; a Shift-click adds the rows from the last click. Escape clears the
// selection. The selected rows (selectedRowIds) have a blue-100 ground and a blue-600 line on the left. The footer is
// never selected. A click on a button or a link in a row does not select it.
// A chip column with hoverCard opens a card for the row (for example the day popup of a night): a pointer on the chip opens
// it, a click or Enter on the chip opens and closes it (a touch screen, the keyboard). A row whose card is null keeps a
// plain chip. The card sits under the chip, right-aligned, and goes above it when the window has no room below; a card
// taller than both sides stays inside the window. It stays open while the pointer is on the chip or on the card; Escape,
// a scroll or a click outside closes it.
// With onCellClick, a number in a clickable column is a text button (a click opens what is behind the number, for example the
// list of the bookings behind a room-night count). The button keeps the look and the tone of the cell; a pointer or the focus
// underlines it. A missing value, a text value, the first column and the footer are never clickable. Enter and Space click.
// The accessible name is "<the first cell of the row> · <the column group> <the column label>: <the value>", or clickLabel.
// With stickyHeader (a table that scrolls with the page, no `size`), the header rows stay at the top of the window while the
// page scrolls down, until the last row passes. A table wider than its frame also gets a second horizontal scrollbar
// directly under the header rows, which stays with them; it and the scrollbar under the last row move the table together.
// The second scrollbar is drawn (a gray track and thumb: a drag of the thumb, or a press on the track beside it).
// The header in view is a copy for the eye and the pointer (aria-hidden): the real header stays in the table under it, for
// a screen reader and the keyboard, and the copy shows the focus of the real header. The copy takes the widths of the real
// columns, so each label stays over its column. Below the sm width (a phone) the table keeps its plain header.

export type DataTableFormat = 'text' | 'date' | 'points' | 'sparkline' | ValueFormat;

export type DataTableColumn = {
	id: string,
	label: string,
	// Default 'text'.
	format?: DataTableFormat,
	// An ISO 4217 code for 'money'. Default 'EUR'.
	currency?: string,
	// Default 0; default 1 for 'points'.
	decimals?: number,
	// Show + before a positive number (a difference).
	signed?: boolean,
	// The chart role of the column: a key line in the header.
	role?: ChartRole,
	// Default true.
	sortable?: boolean,
	// 'change': green for a positive number, red for a negative one, gray for 0 (a pickup).
	// 'band': the fill of the first band (bands) that the value reaches. 'chip': the value in a chip, in the tone of cellTones.
	tone?: 'change' | 'band' | 'chip',
	// tone 'band': the bands from the highest. A value below the last band has no fill.
	bands?: DataTableBand[],
	// tone 'band': a value above this number also gets a red ring (an occupancy above 100 %).
	outlineAbove?: number,
	// 'sparkline': the format of the values for a screen reader. Default 'number'.
	valueFormat?: ValueFormat,
	// An (i) after the label that opens the meaning of the column (InfoTooltip). It sits beside the sort button, not in it.
	info?: { title: string, content: React.ReactNode },
	// tone 'chip': the card of a row (null: that row has no card). The chip becomes a button that opens it.
	hoverCard?: (rowId: string) => React.ReactNode | null,
	// tone 'chip' with hoverCard: the accessible name of the chip button, for example "Day popup of Sun 25 Oct".
	hoverCardLabel?: (rowId: string) => string,
	// With onCellClick on the table: a number in this column is a button. Not the first column.
	clickable?: boolean,
	// clickable: the accessible name of the button of a row. Default "<row> · <column group> <label>: <value>".
	clickLabel?: (row: DataTableRow) => string,
};

// A band of the tone 'band': the cell fill when the value is at least atLeast.
export type DataTableBand = {
	atLeast: number,
	fill: 'green-strong' | 'green' | 'green-light' | 'red-light' | 'red',
};

export type DataTableCellTone = 'success' | 'warning' | 'critical' | 'neutral';

export type DataTableColumnGroup = {
	// Empty text for the columns above which no group label stands (the first column).
	label: string,
	// The number of columns of the group.
	span: number,
};

// A list of values (oldest first) for the format 'sparkline'.
export type DataTableCell = string | number | null | (number | null)[];

export type DataTableMore = {
	// The text of the line, for example "+ 19 more rate plans, included in the total".
	label: string,
	// A text button after the label, for example "Show all 34 rate plans".
	actionLabel?: string,
	onAction?: () => void,
};

// The first rows of each list, in the current order; the rest stay behind a "more" line.
export type DataTableRowLimit = {
	count: number,
	// The text of the line under a cut list ("+ 19 more rate plans, included in the total"). level 0 = the top rows.
	moreLabel: (hidden: number, all: number, level: number) => string,
	// The button that shows the whole list ("Show all 34 rate plans").
	showAllLabel: (all: number, level: number) => string,
	// The button that cuts the list again ("Show the top 15").
	showTopLabel: string,
};

export type DataTableRow = {
	id: string,
	cells: Record<string, DataTableCell>,
	// Rows that this row opens and closes (the segments of a group). A child can have children of its own.
	children?: DataTableRow[],
	// A line after the children of this row when it is open: the rows that the caller left out.
	more?: DataTableMore,
	// A short gray text after the first cell, for example "· 2".
	note?: string,
	// Gray text: a remainder row such as "No segment".
	muted?: boolean,
	// 'current': a blue ground and a blue line on the left (today). 'dimmed': the row at half strength (a past night).
	state?: 'current' | 'dimmed',
	// A gray-100 first cell in bold (a weekend night).
	emphasis?: boolean,
	// The tone of a cell in a column with the tone 'chip', by column id.
	cellTones?: Record<string, DataTableCellTone>,
};

export type DataTableSort = {
	columnId: string,
	direction: 'asc' | 'desc',
};

type Props = {
	columns: DataTableColumn[],
	rows: DataTableRow[],
	// The name of the table for a screen reader.
	ariaLabel: string,
	// The order at the start. Without it, the rows keep the order of the caller.
	defaultSort?: DataTableSort,
	// The table scrolls inside the height of a chart of this size: small 200 px, normal 280 px, large 360 px.
	size?: ChartSize,
	// Default true: a border and a shadow. false inside a card (ChartCard).
	framed?: boolean,
	// The text when there is no row.
	emptyLabel?: string,
	// A header row above the columns. The spans add up to the number of columns.
	columnGroups?: DataTableColumnGroup[],
	// A last row in bold (the total). It does not move when the user sorts.
	footer?: DataTableRow,
	// The ids of the rows with children that are open at the start.
	defaultExpanded?: string[],
	// 'top' (default): a sort orders the top rows; children keep the order of the caller.
	// 'children': the top rows keep the order of the caller; a sort orders the children of each row, at each level.
	sortScope?: 'top' | 'children',
	// 'twoClicks' (default): each click turns the order. 'threeClicks': first click, second click (reversed), then back
	// to defaultSort.
	sortCycle?: 'twoClicks' | 'threeClicks',
	// A line after the top rows, above the footer: the rows that the caller left out.
	more?: DataTableMore,
	// Cut each list to its first rows after the sort, with a line that shows the rest on a click.
	rowLimit?: DataTableRowLimit,
	// A click on the name of a row (not the footer): the row and its level (0 = a top row).
	onRowSelect?: (row: DataTableRow, level: number) => void,
	// The id of the selected row: aria-pressed and a blue name.
	selectedRowId?: string,
	// After a click on a header: the new sort (undefined = the order of the caller) and the ids of the top rows in order.
	onSortChange?: (sort: DataTableSort | undefined, topRowIds: string[]) => void,
	// true: the column labels wrap to more lines, at the bottom of the header row. Default false: one line each.
	wrapHeaders?: boolean,
	// The rows of a multi-row selection (ids at all levels).
	selectedRowIds?: string[],
	// Turns the multi-row selection on: a drag, a click or a Shift-click gives the new list of selected row ids.
	onRowsSelect?: (rowIds: string[]) => void,
	// 'compact': narrower cells (8 px at each side) and a sort icon on the sorted column only, so that a phone shows about 5
	// columns in 360 px. A click on a label still sorts. Default 'comfortable'.
	density?: 'comfortable' | 'compact',
	// A click on a number of a clickable column (not the footer): the row, the column id and the level of the row.
	onCellClick?: (row: DataTableRow, columnId: string, level: number) => void,
	// true: the header rows stay at the top of the window while the page scrolls, with a second horizontal scrollbar under
	// them when the table is wider than its frame. Only without `size`, and from the sm width. Default false.
	stickyHeader?: boolean,
};

const isNumberFormat = (format: DataTableFormat): boolean => format === 'money' || format === 'percent' || format === 'number' || format === 'points';

// A number or a sparkline: right-aligned, the first click sorts the largest first.
const alignsRight = (format: DataTableFormat): boolean => isNumberFormat(format) || format === 'sparkline';

const isMissing = (value: DataTableCell): boolean => value === null || value === undefined || value === ''
	|| (Array.isArray(value) && sparklineChange(value) === null);

// A text in a number column that is not a number ("new"): it shows as it is.
const isText = (value: DataTableCell, column: DataTableColumn): boolean =>
	typeof value === 'string' && isNumberFormat(column.format ?? 'text') && Number.isNaN(Number(value));

const cellText = (value: DataTableCell, column: DataTableColumn): string => {
	if (isMissing(value)) return NO_VALUE;
	const format = column.format ?? 'text';
	if (Array.isArray(value)) {
		const known = value.filter((v): v is number => v !== null);
		const options = { currency: column.currency, decimals: column.decimals };
		const of = (n: number) => formatValue(n, column.valueFormat ?? 'number', options);
		return `From ${of(known[0])} to ${of(known[known.length - 1])}`;
	}
	if (format === 'text') return String(value);
	if (format === 'date') return typeof value === 'string' ? formatStayDateLong(value) : String(value);
	if (isText(value, column)) return value as string;
	// A sparkline column with one value (not a list): the value as it is.
	if (format === 'sparkline') return String(value);
	const n = typeof value === 'number' ? value : Number(value);
	if (format === 'points') return formatPoints(n, column.decimals ?? 1, column.signed === true);
	const options = { currency: column.currency, decimals: column.decimals };
	return column.signed === true ? formatSigned(n, format, options) : formatValue(n, format, options);
};

const BAND_FILL: Record<DataTableBand['fill'], string> = {
	'green-strong': 'bg-green-200 text-green-900', green: 'bg-green-100 text-green-900', 'green-light': 'bg-green-50 text-green-800',
	'red-light': 'bg-red-50 text-red-800', red: 'bg-red-100 text-red-900',
};
const CHIP_TONE: Record<DataTableCellTone, string> = {
	success: 'bg-green-50 text-green-700 ring-green-600/20', warning: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
	critical: 'bg-red-50 text-red-700 ring-red-600/20', neutral: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

const bandClass = (value: DataTableCell, column: DataTableColumn): string => {
	if (typeof value !== 'number') return '';
	const band = (column.bands ?? []).find(b => value >= b.atLeast);
	return classNames(band === undefined ? '' : classNames(BAND_FILL[band.fill], 'font-medium'),
		column.outlineAbove !== undefined && value > column.outlineAbove ? 'ring-2 ring-inset ring-red-600' : '');
};

const toneClass = (value: DataTableCell, column: DataTableColumn): string => {
	if (column.tone === 'band') return bandClass(value, column);
	if (column.tone !== 'change' || typeof value !== 'number') return '';
	const shown = column.format === 'points' ? roundPoints(value, column.decimals ?? 1) : value;
	if (shown > 0) return 'font-medium text-green-700';
	if (shown < 0) return 'font-medium text-red-700';
	return 'text-gray-400';
};

// The value that a sort compares: a sparkline sorts by its change.
const sortValue = (value: DataTableCell): string | number | null =>
	Array.isArray(value) ? sparklineChange(value) : value;

const compare = (x: DataTableCell, y: DataTableCell): number => {
	const a = sortValue(x);
	const b = sortValue(y);
	if (typeof a === 'number' && typeof b === 'number') return a - b;
	return String(a).localeCompare(String(b));
};

// The rows in the order of a sort: the top rows, or (sortScope 'children') the children of each row, at each level.
// A number goes first in both orders, then a text value, then a missing value.
const sortRows = (rows: DataTableRow[], sort: DataTableSort | undefined, columns: DataTableColumn[], sortScope: 'top' | 'children'): DataTableRow[] => {
	if (sort === undefined) return rows;
	const factor = sort.direction === 'asc' ? 1 : -1;
	const column = columns.find(c => c.id === sort.columnId);
	const rank = (v: DataTableCell) => isMissing(v) ? 2 : (column !== undefined && isText(v, column) ? 1 : 0);
	const order = (list: DataTableRow[]) => [...list].sort((r1, r2) => {
		const a = r1.cells[sort.columnId];
		const b = r2.cells[sort.columnId];
		if (rank(a) !== 0 || rank(b) !== 0) return rank(a) - rank(b);
		return compare(a, b) * factor;
	});
	const inside = (list: DataTableRow[]): DataTableRow[] => list.map(row => row.children === undefined ? row
		: { ...row, children: inside(order(row.children)) });
	return sortScope === 'children' ? inside(rows) : order(rows);
};

// The indent of the first cell: a top row, a child, a child of a child.
const INDENT = ['pl-4 sm:pl-6', 'pl-10 sm:pl-12', 'pl-16 sm:pl-20'];
const INDENT_COMPACT = ['pl-3', 'pl-8', 'pl-12'];
// stickyHeader: from the sm width (640 px); a phone keeps the plain header.
const SM_UP = '(min-width: 640px)';
// stickyHeader: the scrollbar under the last row has the look of the drawn second scrollbar and always shows in Chrome, Edge
// and Safari (the style is a WebKit style; Firefox keeps its own). THUMB_MIN: the narrowest thumb of the drawn bar.
const THUMB_MIN = 32;
const SCROLLBAR = '[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 hover:[&::-webkit-scrollbar-thumb]:bg-gray-400';
const indentOf = (level: number, compact = false): string => (compact ? INDENT_COMPACT : INDENT)[Math.min(level, INDENT.length - 1)];

const DataTable: React.FunctionComponent<Props> = ({
	columns, rows, ariaLabel, defaultSort, size, framed = true, emptyLabel = 'No data for this period.',
	columnGroups, footer, defaultExpanded = [], sortScope = 'top', sortCycle = 'twoClicks', more, rowLimit,
	onRowSelect, selectedRowId, onSortChange, wrapHeaders = false, selectedRowIds = [], onRowsSelect, density = 'comfortable',
	onCellClick, stickyHeader = false,
}) => {
	const compact = density === 'compact';
	const cellX = compact ? 'px-2' : 'px-3';
	const [sort, setSort] = useState<DataTableSort | undefined>(defaultSort);
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded));
	// The lists that show all their rows (rowLimit): the id of the row that holds them, TOP for the top rows.
	const [whole, setWhole] = useState<Set<string>>(() => new Set());
	const toggleWhole = (id: string) => setWhole(current => {
		const next = new Set(current);
		if (next.has(id)) next.delete(id); else next.add(id);
		return next;
	});
	const toggle = (id: string) => setExpanded(current => {
		const next = new Set(current);
		if (next.has(id)) next.delete(id); else next.add(id);
		return next;
	});
	// The first column of each group after the first: a thin line on its left side.
	const groupStarts = useMemo(() => {
		const starts = new Set<number>();
		let index = 0;
		(columnGroups ?? []).forEach((group, g) => {
			if (g > 0) starts.add(index);
			index += group.span;
		});
		return starts;
	}, [columnGroups]);
	// The label of the column group of each column (empty without groups), for the name of a clickable cell.
	const groupLabels = useMemo(() => (columnGroups ?? []).flatMap(group => Array<string>(group.span).fill(group.label)), [columnGroups]);

	const sorted = useMemo(() => sortRows(rows, sort, columns, sortScope), [rows, sort, sortScope, columns]);

	// The multi-row selection: the rows in the order that the table shows them, the row of the last click (the anchor of a
	// Shift-click) and the drag in progress.
	const picked = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
	// A line on the left of the first cell marks a current or a selected row; every first cell keeps its place for it.
	const marks = useMemo(() => {
		const any = (list: DataTableRow[]): boolean => list.some(r => r.state === 'current' || any(r.children ?? []));
		return onRowsSelect !== undefined || any(rows);
	}, [rows, onRowsSelect]);
	const shownIds = useRef<string[]>([]);
	const anchor = useRef<string | undefined>(undefined);
	const drag = useRef<{ from: string, moved: boolean } | undefined>(undefined);
	const between = (a: string, b: string): string[] => {
		const ids = shownIds.current;
		const [i, j] = [ids.indexOf(a), ids.indexOf(b)].sort((x, y) => x - y);
		return i < 0 ? [b] : ids.slice(i, j + 1);
	};
	const onRowPointerDown = (event: React.PointerEvent, id: string) => {
		if (onRowsSelect === undefined || event.button !== 0 || (event.target as HTMLElement).closest('button, a, input, select') !== null) return;
		if (event.shiftKey && anchor.current !== undefined) {
			onRowsSelect([...new Set([...selectedRowIds, ...between(anchor.current, id)])]);
			return;
		}
		drag.current = { from: id, moved: false };
	};
	const onRowPointerEnter = (id: string) => {
		if (onRowsSelect === undefined || drag.current === undefined || drag.current.from === id) return;
		drag.current.moved = true;
		onRowsSelect(between(drag.current.from, id));
	};
	useEffect(() => {
		if (onRowsSelect === undefined) return undefined;
		const end = () => {
			const current = drag.current;
			drag.current = undefined;
			if (current === undefined) return;
			anchor.current = current.from;
			if (!current.moved) onRowsSelect(picked.has(current.from) ? selectedRowIds.filter(id => id !== current.from) : [...selectedRowIds, current.from]);
		};
		const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && selectedRowIds.length > 0) onRowsSelect([]); };
		window.addEventListener('pointerup', end);
		window.addEventListener('keydown', escape);
		return () => { window.removeEventListener('pointerup', end); window.removeEventListener('keydown', escape); };
	}, [onRowsSelect, picked, selectedRowIds]);

	// The hover card of a chip column: the row and the column of the open card, and the chip that it hangs from.
	const [card, setCard] = useState<{ rowId: string, columnId: string, chip: HTMLElement, content: React.ReactNode } | null>(null);
	const [cardPlace, setCardPlace] = useState<{ top: number, left: number } | null>(null);
	const cardBox = useRef<HTMLDivElement>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const lastPointer = useRef<string>('');
	const keepCard = () => clearTimeout(closeTimer.current);
	const openCard = (column: DataTableColumn, rowId: string, chip: HTMLElement, content: React.ReactNode) => {
		keepCard();
		if (card !== null && card.rowId === rowId && card.columnId === column.id) return;
		setCardPlace(null);
		setCard({ rowId, columnId: column.id, chip, content });
	};
	const closeCard = (after = 150) => {
		keepCard();
		closeTimer.current = setTimeout(() => setCard(null), after);
	};
	useLayoutEffect(() => {
		if (card === null || cardBox.current === null) return;
		const chip = card.chip.getBoundingClientRect();
		const { width, height } = cardBox.current.getBoundingClientRect();
		const gap = 4, edge = 8;
		const left = Math.max(edge, Math.min(chip.right - width, window.innerWidth - width - edge));
		const below = chip.bottom + gap, above = chip.top - gap - height;
		// Under the chip when it fits, else above it; a card taller than both sides stays inside the window (over the chip).
		const top = below + height <= window.innerHeight - edge ? below
			: above >= edge ? above
				: Math.max(edge, window.innerHeight - edge - height);
		setCardPlace({ top, left });
	}, [card]);
	useEffect(() => {
		if (card === null) return undefined;
		const close = () => { keepCard(); setCard(null); };
		const escape = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			close();
			card.chip.focus();
		};
		const outside = (event: PointerEvent) => {
			const target = event.target as Node;
			if (cardBox.current?.contains(target) === true || card.chip.contains(target)) return;
			close();
		};
		const scroll = (event: Event) => { if (cardBox.current?.contains(event.target as Node) !== true) close(); };
		window.addEventListener('keydown', escape);
		window.addEventListener('pointerdown', outside, true);
		window.addEventListener('scroll', scroll, true);
		window.addEventListener('resize', close);
		return () => {
			window.removeEventListener('keydown', escape);
			window.removeEventListener('pointerdown', outside, true);
			window.removeEventListener('scroll', scroll, true);
			window.removeEventListener('resize', close);
		};
	}, [card]);
	useEffect(() => () => clearTimeout(closeTimer.current), []);

	// stickyHeader: the header copy at the top of the window (from the sm width; a phone keeps the plain header).
	const [smUp, setSmUp] = useState(() => typeof window !== 'undefined' && window.matchMedia(SM_UP).matches);
	useEffect(() => {
		if (!stickyHeader) return undefined;
		const query = window.matchMedia(SM_UP);
		const change = () => setSmUp(query.matches);
		change();
		query.addEventListener('change', change);
		return () => query.removeEventListener('change', change);
	}, [stickyHeader]);
	const pinned = stickyHeader && size === undefined && smUp;
	const scrollBox = useRef<HTMLDivElement>(null);
	const realTable = useRef<HTMLTableElement>(null);
	const realHead = useRef<HTMLTableSectionElement>(null);
	const copyHead = useRef<HTMLDivElement>(null);
	const topBar = useRef<HTMLDivElement>(null);
	const thumb = useRef<HTMLDivElement>(null);
	const thumbDrag = useRef<{ x: number, left: number } | undefined>(undefined);
	// The measures of the real table that the copy follows: the width of each column, the table width, the header height,
	// and the scroll width of the frame (a second scrollbar only when the table is wider than the frame).
	const [fit, setFit] = useState<{ widths: number[], table: number, head: number, scroll: number, frame: number } | null>(null);
	const [focusedHead, setFocusedHead] = useState<string | null>(null);
	useLayoutEffect(() => {
		if (!pinned || realTable.current === null || realHead.current === null || scrollBox.current === null) {
			setFit(null);
			return undefined;
		}
		const table = realTable.current, head = realHead.current, box = scrollBox.current;
		const measure = () => {
			const cells = [...(head.rows[head.rows.length - 1]?.cells ?? [])];
			const next = {
				widths: cells.map(cell => cell.getBoundingClientRect().width),
				table: table.getBoundingClientRect().width,
				head: head.getBoundingClientRect().height,
				scroll: box.scrollWidth,
				frame: box.clientWidth,
			};
			setFit(current => current !== null && current.table === next.table && current.head === next.head
				&& current.scroll === next.scroll && current.frame === next.frame
				&& current.widths.length === next.widths.length && current.widths.every((w, i) => w === next.widths[i]) ? current : next);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(table);
		observer.observe(head);
		observer.observe(box);
		return () => observer.disconnect();
	}, [pinned, columns, columnGroups]);
	const wide = fit !== null && fit.scroll > fit.frame + 1;
	// The second scrollbar is drawn (a track and a thumb), not a native one: a Mac (Safari, Firefox, and Chrome without the
	// scrollbar style) hides a native scrollbar until a scroll, and the bar must always show. The thumb width = the share of
	// the table in view.
	const thumbGeometry = () => {
		const track = topBar.current?.clientWidth ?? 0;
		if (fit === null || track === 0) return { track, width: 0, travel: 0, range: 0 };
		const width = Math.max(THUMB_MIN, Math.round(track * fit.frame / fit.scroll));
		return { track, width, travel: track - width, range: fit.scroll - fit.frame };
	};
	const placeThumb = (left: number) => {
		if (thumb.current === null) return;
		const { width, travel, range } = thumbGeometry();
		thumb.current.style.width = `${width}px`;
		thumb.current.style.transform = `translateX(${range > 0 ? Math.round(left / range * travel) : 0}px)`;
	};
	// The table, the header copy and the second scrollbar follow the one that the user moves.
	const follow = (left: number) => {
		const box = scrollBox.current;
		if (box === null) return;
		if (box.scrollLeft !== left) box.scrollLeft = left;
		const shown = box.scrollLeft;
		if (copyHead.current !== null && copyHead.current.scrollLeft !== shown) copyHead.current.scrollLeft = shown;
		placeThumb(shown);
	};
	useLayoutEffect(() => { if (fit !== null && scrollBox.current !== null) follow(scrollBox.current.scrollLeft); }, [fit, wide]);
	// A drag of the thumb moves the table by the same share; a press on the track beside the thumb moves it by one frame.
	const onThumbDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0 || scrollBox.current === null) return;
		event.preventDefault();
		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		thumbDrag.current = { x: event.clientX, left: scrollBox.current.scrollLeft };
	};
	const onThumbMove = (event: React.PointerEvent<HTMLDivElement>) => {
		const drag = thumbDrag.current;
		if (drag === undefined) return;
		const { travel, range } = thumbGeometry();
		if (travel > 0) follow(drag.left + (event.clientX - drag.x) * range / travel);
	};
	const onThumbUp = (event: React.PointerEvent<HTMLDivElement>) => {
		thumbDrag.current = undefined;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
	};
	const onTrackDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0 || scrollBox.current === null || thumb.current === null || fit === null) return;
		const before = event.clientX < thumb.current.getBoundingClientRect().left;
		follow(scrollBox.current.scrollLeft + (before ? -fit.frame : fit.frame));
	};
	// A sideways wheel or swipe on the header copy moves the table (the copy itself has no scrollbar).
	const onCopyWheel = (event: React.WheelEvent) => {
		const dx = event.deltaX !== 0 ? event.deltaX : (event.shiftKey ? event.deltaY : 0);
		if (dx !== 0 && scrollBox.current !== null) follow(scrollBox.current.scrollLeft + dx);
	};
	// The focus of the real header (under the copy): the copy shows the ring on the same column.
	const onHeadFocus = (event: React.FocusEvent) => {
		const target = event.target as HTMLElement;
		setFocusedHead(target.matches(':focus-visible') ? target.closest('th')?.dataset.column ?? null : null);
	};

	const keyColors = useMemo(() => {
		let seriesIndex = 0;
		return columns.map(c => {
			if (c.role === undefined) return undefined;
			const color = colorOfRole(c.role, seriesIndex);
			if (c.role === 'series') seriesIndex += 1;
			return color;
		});
	}, [columns]);

	const onSort = (column: DataTableColumn) => {
		const first: DataTableSort['direction'] = alignsRight(column.format ?? 'text') ? 'desc' : 'asc';
		const next = ((current: DataTableSort | undefined): DataTableSort | undefined => {
			if (current === undefined || current.columnId !== column.id) return { columnId: column.id, direction: first };
			// The third click (the order is already reversed) goes back to the default order.
			if (sortCycle === 'threeClicks' && current.direction !== first) return defaultSort;
			return { columnId: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' };
		})(sort);
		setSort(next);
		onSortChange?.(next, sortRows(rows, next, columns, sortScope).map(row => row.id));
	};

	// The line sits in the sticky first cell, so it stays in view when the table scrolls sideways; it wraps there.
	const renderMore = (line: DataTableMore, key: string, level: number) =>
		<tr key={`more-${key}`} className={level === 0 ? 'bg-white' : 'bg-gray-50'}>
			<td className={classNames('sticky left-0 bg-inherit py-2.5 text-xs text-gray-500', compact ? 'pr-2' : 'pr-3', indentOf(level, compact), marks ? 'border-l-4 border-l-transparent' : '')}>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					{line.label !== '' && <span>{line.label}</span>}
					{line.actionLabel !== undefined && <button type="button" onClick={line.onAction}
						className="whitespace-nowrap rounded font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
						{line.actionLabel}
					</button>}
				</div>
			</td>
			{columns.length > 1 && <td colSpan={columns.length - 1} />}
		</tr>;

	const renderRow = (row: DataTableRow, kind: 'top' | 'child' | 'footer', hasChildren: boolean, open: boolean, level = kind === 'child' ? 1 : 0) => {
		const selectable = onRowSelect !== undefined && kind !== 'footer';
		const selected = selectable && row.id === selectedRowId;
		const multi = onRowsSelect !== undefined && kind !== 'footer';
		const inSelection = multi && picked.has(row.id);
		if (multi) shownIds.current.push(row.id);
		// The ground of a selected or a current row sits on its cells: the zebra rule of the table paints the even rows.
		const rowFill = inSelection ? 'bg-blue-100' : row.state === 'current' ? 'bg-blue-50' : '';
		const dimmed = row.state === 'dimmed' && !inSelection;
		return <tr key={`${kind}-${row.id}`} aria-selected={multi ? inSelection : undefined}
			onPointerDown={multi ? event => onRowPointerDown(event, row.id) : undefined}
			onPointerEnter={multi ? () => onRowPointerEnter(row.id) : undefined}
			className={classNames(kind === 'top' ? 'bg-white' : 'bg-gray-50',
				kind === 'footer' ? 'border-t border-gray-300' : '', multi ? 'cursor-pointer select-none' : '')}>
			{columns.map((column, i) => {
				const value = row.cells[column.id];
				const first = i === 0;
				// The card of a chip: a row with no card (null) keeps a plain chip.
				const cardContent = column.tone === 'chip' && column.hoverCard !== undefined && kind !== 'footer' && !isMissing(value) ? column.hoverCard(row.id) : null;
				return <td key={column.id}
					className={classNames(
						'whitespace-nowrap py-2.5 text-xs',
						first ? classNames('sticky left-0 font-medium', compact ? 'pr-2' : 'pr-3', rowFill !== '' ? rowFill : 'bg-inherit', indentOf(level, compact)) : cellX,
						!first && rowFill !== '' && !(column.tone === 'band' && typeof value === 'number') ? rowFill : '',
						kind === 'footer' ? 'font-semibold text-gray-900'
							: (first ? (row.muted === true || kind === 'child' ? 'text-gray-500' : 'text-gray-900') : 'text-gray-500'),
						alignsRight(column.format ?? 'text') ? 'text-right tabular-nums' : 'text-left',
						groupStarts.has(i) ? 'border-l border-gray-200' : '',
						kind !== 'footer' && !first ? toneClass(value, column) : '',
						first && marks ? classNames('border-l-4', inSelection ? 'border-l-blue-600' : row.state === 'current' ? 'border-l-blue-300' : 'border-l-transparent') : '',
						first && row.emphasis === true && !inSelection && row.state !== 'current' ? classNames('!bg-gray-100 font-semibold', dimmed ? '' : '!text-gray-900') : '',
						// A dimmed row at half strength by the colour opacity, not by `opacity`: an opacity paints a cell over the sticky
						// first cell when the table scrolls sideways.
						dimmed ? (first ? '!text-gray-400' : 'bg-opacity-50 text-opacity-50') : '',
					)}>
					{first && selectable
						? <span className="-ml-1 inline-flex items-center gap-x-1">
							{hasChildren
								? <button type="button" aria-expanded={open} aria-label={`${open ? 'Close' : 'Open'} ${cellText(value, column)}`}
									onClick={() => toggle(row.id)}
									className="rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
									{open
										? <ChevronDownIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
										: <ChevronRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />}
								</button>
								: null}
							<button type="button" aria-pressed={selected} onClick={() => onRowSelect?.(row, level)}
								className={classNames('rounded text-left underline-offset-2 hover:text-blue-600 hover:underline focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
									selected ? 'text-blue-700 underline' : '')}>
								{cellText(value, column)}
							</button>
							{row.note !== undefined && <span className="font-normal text-gray-400">{row.note}</span>}
						</span>
					: Array.isArray(value) && !isMissing(value)
						// The screen reader text (sr-only, absolute) stays inside the scroll frame, which is `relative`. The cell itself has
						// no position: a positioned cell painted its line over the sticky first column when the table scrolled sideways.
						? <span className="inline-flex items-center justify-end">
							<Sparkline values={value} variant="direction" className="block h-4 w-20" />
							<span className="sr-only">{cellText(value, column)}</span>
						</span>
					: first && hasChildren
						? <button type="button" aria-expanded={open} onClick={() => toggle(row.id)}
							className="-ml-1 inline-flex items-center gap-x-1 rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
							{open
								? <ChevronDownIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
								: <ChevronRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />}
							<span>{cellText(value, column)}</span>
							{row.note !== undefined && <span className="font-normal text-gray-400">{row.note}</span>}
						</button>
						: cardContent !== null && cardContent !== undefined
							? <button type="button" aria-haspopup="dialog" aria-expanded={card?.rowId === row.id && card.columnId === column.id}
								aria-label={column.hoverCardLabel?.(row.id)}
								onPointerEnter={event => { if (event.pointerType === 'mouse') openCard(column, row.id, event.currentTarget, cardContent); }}
								onPointerLeave={event => { if (event.pointerType === 'mouse') closeCard(); }}
								onPointerDown={event => { lastPointer.current = event.pointerType; }}
								onClick={event => {
									// A mouse opened the card on the hover: its click keeps it. A tap or Enter opens and closes it.
									const byMouse = lastPointer.current === 'mouse' && event.detail > 0;
									if (card?.rowId === row.id && card.columnId === column.id && !byMouse) { closeCard(0); return; }
									openCard(column, row.id, event.currentTarget, cardContent);
								}}
								className={classNames('inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 font-semibold tabular-nums ring-1 ring-inset hover:ring-2 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
									CHIP_TONE[row.cellTones?.[column.id] ?? 'neutral'], dimmed ? 'bg-opacity-50 text-opacity-50' : '')}>{cellText(value, column)}</button>
						: onCellClick !== undefined && column.clickable === true && !first && kind !== 'footer' && !isMissing(value) && !isText(value, column)
							&& column.tone !== 'chip'
							? <button type="button" onClick={() => onCellClick(row, column.id, level)}
								aria-label={column.clickLabel?.(row)
									?? `${cellText(row.cells[columns[0].id], columns[0])} · ${[groupLabels[i] ?? '', column.label].filter(t => t !== '').join(' ')}: ${cellText(value, column)}`}
								className="rounded tabular-nums underline-offset-2 hover:text-blue-600 hover:underline focus:outline-none focus-visible:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
								{cellText(value, column)}
							</button>
						: column.tone === 'chip' && !isMissing(value) && kind !== 'footer'
							? <span className={classNames('inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 font-semibold tabular-nums ring-1 ring-inset',
								CHIP_TONE[row.cellTones?.[column.id] ?? 'neutral'], dimmed ? 'bg-opacity-50 text-opacity-50' : '')}>{cellText(value, column)}</span>
						: <>
							{cellText(value, column)}
							{first && row.note !== undefined && <span className="ml-1 font-normal text-gray-400">{row.note}</span>}
						</>}
				</td>;
			})}
		</tr>;
	};

	// A list cut by rowLimit: its first rows, and the line under it (none when the list is not longer than the limit).
	const TOP = '\u0000top';
	const limited = (list: DataTableRow[], owner: string, level: number): { shown: DataTableRow[], line?: React.JSX.Element } => {
		if (rowLimit === undefined || list.length <= rowLimit.count) return { shown: list };
		const all = whole.has(owner);
		const line: DataTableMore = all
			? { label: '', actionLabel: rowLimit.showTopLabel, onAction: () => toggleWhole(owner) }
			: { label: rowLimit.moreLabel(list.length - rowLimit.count, list.length, level), actionLabel: rowLimit.showAllLabel(list.length, level), onAction: () => toggleWhole(owner) };
		return { shown: all ? list : list.slice(0, rowLimit.count), line: renderMore(line, `limit-${owner}`, level) };
	};

	// A row, then (when it is open) its children with their own children, then its "more" lines.
	const linesOf = (row: DataTableRow, level: number): React.JSX.Element[] => {
		const hasChildren = (row.children ?? []).length > 0;
		const open = hasChildren && expanded.has(row.id);
		const lines = [renderRow(row, level === 0 ? 'top' : 'child', hasChildren, open, level)];
		if (open) {
			const { shown, line } = limited(row.children ?? [], row.id, level + 1);
			shown.forEach(child => lines.push(...linesOf(child, level + 1)));
			if (line !== undefined) lines.push(line);
			if (row.more !== undefined) lines.push(renderMore(row.more, row.id, level + 1));
		}
		return lines;
	};
	shownIds.current = [];
	const top = limited(sorted, TOP, 0);

	// The header rows: in the table, and (stickyHeader) once more in the copy at the top of the window.
	const headRows = (copy: boolean) => <>
			{columnGroups !== undefined && <tr>
				{columnGroups.map((group, g) => <th key={`${group.label}-${g}`} scope="colgroup" colSpan={group.span}
					className={classNames('whitespace-nowrap pt-2.5 pb-1 text-center text-xs font-semibold uppercase tracking-wide text-gray-500', cellX,
						g > 0 ? 'border-l border-gray-200' : (group.span === 1 ? 'sticky left-0 z-10 bg-white' : ''))}>
					{group.label}
				</th>)}
			</tr>}
			<tr>
				{columns.map((column, i) => {
					const isNumber = alignsRight(column.format ?? 'text');
					const active = sort !== undefined && sort.columnId === column.id;
					const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
					const SortIcon = !active ? ChevronUpDownIcon : (sort.direction === 'asc' ? ChevronUpIcon : ChevronDownIcon);
					const key = keyColors[i];
					// wrapHeaders: the key line is inline and stays on one line with the first word, so it keys a label of more
					// lines and never stands alone on a line.
					const [firstWord, ...otherWords] = column.label.split(' ');
					const label = wrapHeaders
						? <span className={classNames('block', isNumber ? 'text-right' : 'text-left')}>
							<span className="whitespace-nowrap">
								{key !== undefined && <span aria-hidden="true" className="mr-1.5 inline-block h-0.5 w-3 rounded-full align-middle" style={{ background: key }} />}
								{firstWord}
							</span>
							{otherWords.length > 0 && ` ${otherWords.join(' ')}`}
						</span>
						: <span className={classNames('flex items-center gap-x-1.5', column.sortable === false && isNumber ? 'justify-end' : '')}>
							{key !== undefined && <span aria-hidden="true" className="inline-block h-0.5 w-3 shrink-0 rounded-full" style={{ background: key }} />}
							{column.label}
						</span>;
					return <th key={column.id} scope="col" aria-sort={column.sortable === false ? undefined : ariaSort} data-column={column.id}
						className={classNames(
							wrapHeaders ? 'whitespace-normal align-bottom' : 'whitespace-nowrap',
							'py-2.5 text-xs font-semibold text-gray-900',
							i === 0 ? classNames('sticky left-0 z-10 bg-white', compact ? 'pl-3 pr-2' : 'pl-4 pr-3 sm:pl-6', marks ? 'border-l-4 border-l-transparent' : '') : cellX,
							isNumber ? 'text-right' : 'text-left',
							groupStarts.has(i) ? 'border-l border-gray-200' : '',
						)}>
						{(() => {
							const sortable = column.sortable === false
								? label
								: <button type="button" onClick={() => onSort(column)} tabIndex={copy ? -1 : undefined}
									className={classNames(
										'group flex items-center gap-x-1 rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
										copy && focusedHead === column.id ? 'outline outline-2 outline-offset-2 outline-blue-600' : '',
										column.info === undefined ? 'w-full' : '',
										isNumber ? 'flex-row-reverse' : '',
									)}>
									{label}
									{(active || !compact) && <SortIcon aria-hidden="true" className={classNames('h-4 w-4 shrink-0', active ? 'text-gray-700' : 'text-gray-300 group-hover:text-gray-500')} />}
								</button>;
							// info: the (i) follows the label; a number column keeps its label on the right edge.
							return column.info === undefined ? sortable
								: <span className={classNames('flex items-center gap-x-1', isNumber ? 'justify-end' : '')}>
									{sortable}
									{/* The header text is bold and can be right-aligned: the meaning reads as normal, left-aligned text. */}
									<span className="text-left font-normal"><InfoTooltip label={`What ${column.info.title} means`} title={column.info.title} focusable={!copy}>{column.info.content}</InfoTooltip></span>
								</span>;
						})()}
					</th>;
				})}
			</tr>
	</>;

	// stickyHeader: the frame clips (`overflow-clip`) and does not scroll, so the header copy sticks to the window.
	return <div className={classNames(framed ? 'rounded-lg border border-gray-200 bg-white shadow-sm' : '', pinned ? 'overflow-clip' : 'overflow-hidden')}>
		{/* The copy (z-20) lies over the table box (z-0: its own stacking layer, so no cell of the table can come above it). */}
		{pinned && fit !== null && <div aria-hidden="true" className="sticky top-0 z-20 border-b border-gray-300 bg-white">
			<div ref={copyHead} onWheel={onCopyWheel} className="overflow-hidden bg-white">
				<table className="table-fixed divide-y divide-gray-300" style={{ width: fit.table }}>
					<colgroup>{fit.widths.map((width, i) => <col key={columns[i]?.id ?? i} style={{ width }} />)}</colgroup>
					{/* The class of the real header, so the copy looks the same (its lines behind the blur of the class). */}
					<thead className="sticky-table-header">{headRows(true)}</thead>
				</table>
			</div>
			{wide && <div ref={topBar} onPointerDown={onTrackDown} onWheel={onCopyWheel} className="relative h-2.5 bg-gray-100">
				<div ref={thumb} onPointerDown={onThumbDown} onPointerMove={onThumbMove} onPointerUp={onThumbUp} onPointerCancel={onThumbUp}
					className="absolute inset-y-0 left-0 touch-none rounded-full bg-gray-300 hover:bg-gray-400 active:bg-gray-400" />
			</div>}
		</div>}
		{/* stickyHeader: the real header and its bottom line slide under the copy, so the first row starts right under the
			line of the copy (one line, as without the copy). */}
		<div ref={scrollBox} onScroll={pinned ? event => follow(event.currentTarget.scrollLeft) : undefined}
			className={classNames('relative overflow-auto', pinned ? 'z-0' : '', pinned && wide ? SCROLLBAR : '')}
			style={size !== undefined ? { maxHeight: CHART_HEIGHT[size] } : (pinned && fit !== null ? { marginTop: -fit.head - 1 } : undefined)}>
			<table ref={realTable} className="min-w-full divide-y divide-gray-300">
				<caption className="sr-only">{ariaLabel}</caption>
				<thead ref={realHead} className="sticky-table-header" onFocus={pinned ? onHeadFocus : undefined} onBlur={pinned ? () => setFocusedHead(null) : undefined}>
					{headRows(false)}
				</thead>
				<tbody className="divide-y divide-gray-200">
					{sorted.length === 0
						? <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500 sm:px-6">{emptyLabel}</td></tr>
						: [...top.shown.flatMap(row => linesOf(row, 0)), ...(top.line === undefined ? [] : [top.line])]}
					{more !== undefined && sorted.length > 0 && renderMore(more, 'table', 0)}
					{footer !== undefined && sorted.length > 0 && renderRow(footer, 'footer', false, false)}
				</tbody>
			</table>
		</div>
		{card !== null && createPortal(
			// The card hangs from the chip: its place depends on where the chip is in the window, so it needs a style.
			<div ref={cardBox} role="dialog" aria-label={columns.find(c => c.id === card.columnId)?.hoverCardLabel?.(card.rowId)}
				onPointerEnter={event => { if (event.pointerType === 'mouse') keepCard(); }}
				onPointerLeave={event => { if (event.pointerType === 'mouse') closeCard(); }}
				className={classNames('fixed z-50', cardPlace === null ? 'invisible' : '')}
				style={cardPlace === null ? { top: 0, left: 0 } : { top: cardPlace.top, left: cardPlace.left }}>
				{card.content}
			</div>, document.body)}
	</div>;
};

export default DataTable;
